import { Fragment, useState, useRef, useEffect } from 'react'
import { useQuery } from 'react-query'
import { getOrgDetails } from '@src/api/generic'
import { getOrgs } from '@src/api/admin'
import { FormGroup, Label } from 'reactstrap'
import { setGlobalOrg, getGlobalOrg, isSpAdmin, isOrgAdmin, isVenueAdmin, isMarker, isFinAdmin, isCoach, getIDTokenName, selectStyles } from '@utils'
import useAuthStatus from '@hooks/useAuthStatus'
import Select from 'react-select'
import navigation from '@src/navigation/vertical'
import PerfectScrollbar from 'react-perfect-scrollbar'
import VerticalMenuHeader from './VerticalMenuHeader'
import VerticalNavMenuItems from './VerticalNavMenuItems'
import classnames from 'classnames'

const Sidebar = props => {
  const { loggedIn, checkLogin } = useAuthStatus()
  const { menuCollapsed, routerProps, menu, currentActiveItem, skin } = props
  const [groupOpen, setGroupOpen] = useState([])
  const [groupActive, setGroupActive] = useState([])
  const [activeItem, setActiveItem] = useState(null)
  const [navigationItems, setNavigationItems] = useState([])
  const [menuHover, setMenuHover] = useState(false)
  const shadowRef = useRef(null)
  const onMouseEnter = () => { if (menuCollapsed) setMenuHover(true) }
  const [orgs, setOrgs] = useState([])
  const [orgId, setOrgId] = useState(getGlobalOrg())

  const { data: oData } = useQuery(['orgs'], () => getOrgs(), { enabled: isSpAdmin() === true })
  useEffect(() => { if (oData && oData.data) setOrgs(oData.data.map(v => ({ label: v.name, value: v.id }))) }, [oData])
  useEffect(() => { if (orgId && orgId !== getGlobalOrg()) setGlobalOrg(orgId) }, [orgId])
  useEffect(() => { if (getGlobalOrg() > 0 && orgId !== getGlobalOrg()) setOrgId(getGlobalOrg()) }, [getGlobalOrg()])

  const scrollMenu = c => {
    if (shadowRef && c.scrollTop > 0) {
      if (!shadowRef.current.classList.contains('d-block')) shadowRef.current.classList.add('d-block')
    } else if (shadowRef.current.classList.contains('d-block')) shadowRef.current.classList.remove('d-block')
  }

  const fetchNavigationItems = (org) => {
    let nav = (!checkLogin && loggedIn) ? navigation.filter(c => c.id !== 'login') : navigation.filter(c => c.id !== 'logout')
    if (isSpAdmin()) setNavigationItems(nav)
    else {
      if (isOrgAdmin()) nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'shareplay-management') })
      else if (isVenueAdmin()) nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'shareplay-management' && c.id !== 'org-management') })
      else if (isMarker()) nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'shareplay-management' && c.id !== 'org-management' && c.id !== 'member-management' && c.id !== 'venue-management' && c.id !== 'coaching-management' && c.id !== 'reports') })
      else if (isFinAdmin()) nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'shareplay-management' && c.id !== 'org-management' && c.id !== 'member-management' && c.id !== 'venue-management' && c.id !== 'booking-management' && c.id !== 'coaching-management') })
      if (!isCoach() || org.plan?.module === 'PLAY') nav = nav.filter(n => n.id !== 'coach')
      if (!isOrgAdmin() && !isVenueAdmin() && !isMarker() && !isFinAdmin()) nav = nav.filter(n => n.id !== 'admin')
      if (org.plan && (isOrgAdmin() || isVenueAdmin() || isMarker() || isFinAdmin())) {
        if (org.plan.module === 'PLAY') nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'coaching-management') })
        if (org.plan.module === 'LEARN') nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'booking-management' && c.id !== 'venue-management') })
      }
      if (org.inactive) {
        nav.forEach(n => { if (n.id === 'admin') n.children = n.children.filter(c => c.id !== 'shareplay-management' && c.id !== 'org-management' && c.id !== 'member-management' && c.id !== 'venue-management' && c.id !== 'coaching-management' && c.id !== 'booking-management') })
        nav = nav.filter(n => (n.id === 'admin' && n.children && n.children.length > 0) || (n.id !== 'admin' && n.id !== 'coach'))
      }
      setNavigationItems(nav)
    }
  }

  const { data: odData } = useQuery(['orgDetails', getGlobalOrg()], () => getOrgDetails(getGlobalOrg()), { enabled: !!getGlobalOrg() })
  useEffect(() => { if (odData && odData.data) fetchNavigationItems(odData.data); else fetchNavigationItems({ inactive: false }) }, [odData, loggedIn, checkLogin])

  const getRole = () => {
    if (isSpAdmin()) return 'Sply Admin'
    if (isOrgAdmin()) return 'Org Admin'
    if (isVenueAdmin()) return 'Venue Admin'
    if (isMarker()) return 'Marker'
    if (isFinAdmin()) return 'Fin Admin'
    if (isCoach()) return 'Coach'
    return 'End User'
  }

  const handleOrg = id => {
    setOrgId(id)
    setTimeout(() => window.location.reload(), 500)
  }

  return <Fragment>
    <div className={classnames('main-menu menu-fixed menu-accordion menu-shadow', {
      expanded: menuHover || menuCollapsed === false,
      'menu-light': skin !== 'semi-dark' && skin !== 'dark',
      'menu-dark': skin === 'semi-dark' || skin === 'dark'
    })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={() => setMenuHover(false)}>
      {menu ? menu(props) : <>
        <VerticalMenuHeader setGroupOpen={setGroupOpen} menuHover={menuHover} {...props} />
        <div className='shadow-bottom' ref={shadowRef}></div>
        <PerfectScrollbar
          className='main-menu-content d-flex flex-column justify-content-between'
          options={{ wheelPropagation: false }}
          onScrollY={container => scrollMenu(container)}>
          <div className='d-flex flex-column'>
            <ul className='navigation navigation-main'>
              <VerticalNavMenuItems
                items={navigationItems}
                groupActive={groupActive}
                setGroupActive={setGroupActive}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                groupOpen={groupOpen}
                setGroupOpen={setGroupOpen}
                routerProps={routerProps}
                menuCollapsed={menuCollapsed}
                menuHover={menuHover}
                currentActiveItem={currentActiveItem}
              />
            </ul>
            {isSpAdmin() && <FormGroup className='mx-1'>
              <Label for='org'>Org:</Label>
              <Select
                isMulti={false}
                searchable
                id='org'
                name='org'
                options={orgs}
                value={orgs.filter(o => o.value === orgId)}
                styles={selectStyles()}
                menuPortalTarget={document.modal}
                onChange={d => handleOrg(d.value)}
              />
            </FormGroup>}
          </div>
          {!checkLogin && loggedIn && (isSpAdmin() || isOrgAdmin() || isVenueAdmin() || isMarker() || isFinAdmin() || isCoach()) && <ul>
            <div className='d-flex flex-column ljustify-content-center align-items-center mb-1'>
              <p className='font-small-2 text-muted mb-0'>
                Logged in as <span className='c-heading'>{getIDTokenName()}</span> [{getRole()}]
              </p>
            </div>
          </ul>}
        </PerfectScrollbar>
      </>}
    </div>
  </Fragment>
}

export default Sidebar
